import type {
  CreateTemplateDto,
  CreateTemplateVersionDto,
  Page,
  Template,
  TemplatesQuery,
  TemplateVersion,
  TemplateVersionsQuery,
  UUID,
  VersionContent,
} from "../../interface/publications"
import { businessApi, postBotTags, requestQuery } from "./api"
import { isUrlButtonStyle } from "@core/components/telegram/buttons/urlButtonStyle"

const contentBody = (content: VersionContent): VersionContent => {
  if (content.parseMode !== undefined && content.entities !== undefined)
    throw new TypeError("Use parseMode or entities, not both")
  const common = {
    text: content.text,
    mediaIds: content.mediaIds,
    buttons: content.buttons.map((row) =>
      row.map((button) => {
        if (button.style !== undefined && !isUrlButtonStyle(button.style))
          throw new TypeError(
            "Button style must be primary, success or danger, or omitted",
          )
        return {
          text: button.text,
          url: button.url,
          ...(button.style !== undefined ? { style: button.style } : {}),
        }
      }),
    ),
  }
  if (content.parseMode !== undefined)
    return { ...common, parseMode: content.parseMode }
  // Native entities and complete User metadata are deliberately not narrowed.
  if (content.entities !== undefined)
    return { ...common, entities: content.entities }
  return common
}

export const templatesApi = businessApi.injectEndpoints({
  endpoints: (build) => ({
    createTemplate: build.mutation<Template, CreateTemplateDto>({
      query: (body) => ({
        url: "templates",
        method: "POST",
        body: { name: body.name },
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [postBotTags.templates],
    }),
    getTemplates: build.query<Page<Template>, TemplatesQuery | void>({
      query: (args) => ({
        url: "templates",
        params: { page: args?.page, limit: args?.limit, search: args?.search },
      }),
      providesTags: (result) => [
        postBotTags.templates,
        ...(result?.data.map(({ id }) => postBotTags.template(id)) ?? []),
      ],
    }),
    getTemplate: build.query<Template, UUID>({
      query: (id) => `templates/${id}`,
      providesTags: (_result, _error, id) => [postBotTags.template(id)],
    }),
    createTemplateVersion: build.mutation<
      TemplateVersion,
      { templateId: UUID; body: CreateTemplateVersionDto }
    >({
      queryFn: requestQuery(({ templateId, body }) => ({
        url: `templates/${templateId}/versions`,
        method: "POST",
        body: { content: contentBody(body.content) },
      })),
      invalidatesTags: (_result, error, { templateId }) =>
        error
          ? []
          : [
              postBotTags.templates,
              postBotTags.template(templateId),
              postBotTags.versions(templateId),
            ],
    }),
    getTemplateVersions: build.query<
      Page<TemplateVersion>,
      TemplateVersionsQuery
    >({
      query: ({ templateId, page, limit }) => ({
        url: `templates/${templateId}/versions`,
        params: { page, limit },
      }),
      providesTags: (result, _error, { templateId }) => [
        postBotTags.versions(templateId),
        ...(result?.data.map(({ id }) => postBotTags.version(id)) ?? []),
      ],
    }),
    getTemplateVersion: build.query<TemplateVersion, UUID>({
      query: (id) => `template-versions/${id}`,
      providesTags: (_result, _error, id) => [postBotTags.version(id)],
    }),
  }),
})

export const {
  useCreateTemplateMutation,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateVersionMutation,
  useGetTemplateVersionsQuery,
  useGetTemplateVersionQuery,
  useLazyGetTemplateVersionQuery,
} = templatesApi
